/**
 * Documents global error catcher behavior for ERP Accelerator.
 */

"use client";

import React, { useEffect } from 'react';
import { useBanner } from './BannerProvider';

/**
 * GlobalErrorCatcher attaches window-level listeners to surface unexpected
 * errors to the user as top-right banners. It is not a replacement for
 * explicit, contextual error handling in components, but provides a
 * safety net for unhandled errors and promise rejections.
 *
 * Also listens for a custom event `app:error` to allow any code to
 * programmatically trigger a banner: `document.dispatchEvent(new CustomEvent('app:error', { detail: { title, message } }))`
 */
const GlobalErrorCatcher: React.FC = () => {
  const { showBanner } = useBanner();

  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason: any = event.reason;
      const message = typeof reason === 'string' ? reason : reason?.message || 'An unexpected error occurred';
      showBanner({ type: 'error', title: 'Unexpected error', message, duration: 6000 });
    };

    const onError = (event: ErrorEvent) => {
      const message = event?.message || 'An unexpected error occurred';
      showBanner({ type: 'error', title: 'Unexpected error', message, duration: 6000 });
    };

    const onAppError = (e: Event) => {
      const detail: any = (e as CustomEvent).detail || {};
      const title = detail.title || 'Error';
      const message = detail.message || 'An unexpected error occurred';
      const type = detail.type || 'error';
      const duration = detail.duration || 5000;
      showBanner({ type, title, message, duration });
    };

    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('error', onError);
    document.addEventListener('app:error', onAppError as EventListener);

    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('error', onError);
      document.removeEventListener('app:error', onAppError as EventListener);
    };
  }, [showBanner]);

  return null;
};

export default GlobalErrorCatcher;
